import React, { useState } from 'react';
import { BygrProfileDetails, InvitationStatus, ProfileInvite } from 'types';
import { createDataTable } from 'components/DataTable';
import { Button, ConfirmButton, IconToast, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import InviteProfileMemberModal from '../InviteProfileMemberModal';
import { useAsyncState } from 'utils/hooks';
import { getSystemInfo } from 'services/system';
import { cancelProfileInvitation, resendProfileInvitation } from 'services/profile-invitations';

interface Props {
    profile: BygrProfileDetails;
    reloadProfile(): void;
}
const InviteDataTable = createDataTable<ProfileInvite>();

const ProfileInvites: React.FC<Props> = ({ profile, reloadProfile }) => {
    const [systemInfo] = useAsyncState(() => getSystemInfo(), [], null);

    const [isLoading, setIsLoading] = useState(false);
    const [inviteTableState, setInviteTableState] = useState(InviteDataTable.getInitialState('id'));

    const [inviteModalIsOpen, setInviteModalIsOpen] = useState(false);

    if (!profile) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <InviteDataTable
                onStateChange={setInviteTableState}
                state={inviteTableState}
                data={profile.invitations}
                totalItemCount={profile.invitations.length}
                className="table style-3">
                <InviteDataTable.Column header="Invite Id" field="id" renderer={({ value }) => value} />
                <InviteDataTable.Column header="Email" field="email" renderer={({ value }) => value} />
                <InviteDataTable.Column header="First Name" field="firstName" renderer={({ value }) => value} />
                <InviteDataTable.Column header="Last Name" field="lastName" renderer={({ value }) => value} />
                <InviteDataTable.Column
                    header="Is Admin"
                    field="isAdministrator"
                    renderer={({ value }) => (value ? 'Yes' : 'No')}
                />
                <InviteDataTable.Column header="Status" field="status" renderer={({ value }) => value} />
                <InviteDataTable.Column
                    header="Copy invitation link"
                    field="token"
                    renderer={({ value, row }) => (
                        <Button
                            // disabled={row.status !== InvitationStatus.Sent}
                            loading={isLoading}
                            onClick={async () => {
                                let inviteUrl = `${systemInfo?.frontendUrl}/cm/invitation?token=${value}`;

                                navigator.clipboard.writeText(inviteUrl);
                                IconToast.success('Copied invitation link to clipboard');
                            }}
                            color="tertiary">
                            Copy invitation link
                        </Button>
                    )}
                />
                <InviteDataTable.Column
                    header="Resend invitation"
                    field="status"
                    renderer={({ value, row }) =>
                        value !== InvitationStatus.Registered && (
                            <Button
                                disabled={row.status !== InvitationStatus.Sent}
                                loading={isLoading}
                                onClick={async () => {
                                    setIsLoading(true);
                                    await resendProfileInvitation(row.id);
                                    setIsLoading(false);
                                    IconToast.success('Resent invitation');
                                }}
                                color="tertiary">
                                Resend invitation
                            </Button>
                        )
                    }
                />
                <InviteDataTable.Column
                    header="Cancel invitation"
                    field="status"
                    renderer={({ value, row }) =>
                        value !== InvitationStatus.Registered && (
                            <ConfirmButton
                                disabled={row.status === InvitationStatus.Canceled}
                                dialogTitle="Are you sure?"
                                dialogBody="Are you sure, this cannot be reverted."
                                loading={isLoading}
                                onConfirm={async () => {
                                    setIsLoading(true);
                                    await cancelProfileInvitation(row.id);
                                    reloadProfile();
                                    setIsLoading(false);
                                    IconToast.success('Invitation canceled');
                                }}
                                color="danger">
                                Cancel invitation
                            </ConfirmButton>
                        )
                    }
                />
            </InviteDataTable>
            <Button onClick={() => setInviteModalIsOpen(true)} color="tertiary">
                Invite user
            </Button>

            <InviteProfileMemberModal
                isOpen={inviteModalIsOpen}
                profile={profile}
                toggle={() => setInviteModalIsOpen(false)}
                onSave={() => {
                    reloadProfile();
                }}
            />
        </>
    );
};

export default ProfileInvites;
